import { tokens } from '@/lib/tokens.js';
import { useAdminTemplate } from '../../hooks/use-admin-templates.js';
import { StatusBadge } from '../shared/status-badge.js';

const styles = {
  container: {
    backgroundColor: tokens.colors.bgCardDark,
    borderRadius: tokens.radius.card,
    boxShadow: '0 4px 6px -1px rgba(0,0,0,.1)',
    overflow: 'hidden',
  },
  header: {
    padding: '1rem 1.5rem',
    borderBottom: `1px solid ${tokens.colors.borderDark}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  title: {
    fontSize: '1.125rem',
    fontWeight: 700,
    color: tokens.colors.textDark,
    margin: 0,
    fontFamily: 'monospace',
  },
  body: { padding: '1.5rem' },
  meta: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '1rem',
    marginBottom: '1.5rem',
  },
  metaItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.25rem',
  },
  metaLabel: {
    fontSize: '0.75rem',
    color: tokens.colors.textMutedDark,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  metaValue: {
    fontSize: '0.875rem',
    color: tokens.colors.textDark,
  },
  sectionTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: tokens.colors.textMutedDark,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    marginBottom: '0.5rem',
  },
  pre: {
    backgroundColor: '#0f172a',
    border: `1px solid ${tokens.colors.borderDark}`,
    borderRadius: tokens.radius.card,
    padding: '1rem',
    overflow: 'auto',
    fontSize: '0.8125rem',
    color: '#e2e8f0',
    fontFamily: 'monospace',
    maxHeight: '400px',
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-word' as const,
  },
  actions: {
    display: 'flex',
    gap: '0.75rem',
    marginTop: '1.5rem',
  },
  btnDanger: {
    backgroundColor: tokens.colors.error,
    color: '#fff',
    border: 'none',
    borderRadius: tokens.radius.button,
    padding: '0.5rem 1rem',
    fontSize: '0.875rem',
    cursor: 'pointer',
    fontWeight: 500,
  },
};

interface TemplateDetailProps {
  templateId: string;
  onDeprecateClick?: () => void;
}

export function TemplateDetail({ templateId, onDeprecateClick }: TemplateDetailProps) {
  const { data, isLoading, isError } = useAdminTemplate(templateId);

  if (isLoading) {
    return (
      <output
        style={{
          display: 'block',
          padding: '2rem',
          textAlign: 'center',
          color: tokens.colors.textMutedDark,
        }}
        aria-label="Loading template"
      >
        Loading…
      </output>
    );
  }

  if (isError || !data) {
    return (
      <div role="alert" style={{ color: tokens.colors.error, padding: '1rem' }}>
        Error loading template
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>{data.code}</h2>
          <span style={{ fontSize: '0.875rem', color: tokens.colors.textMutedDark }}>
            v{data.version} · {data.kind} · {data.category}
          </span>
        </div>
        <StatusBadge status={data.status} />
      </div>
      <div style={styles.body}>
        <div style={styles.meta}>
          <div style={styles.metaItem}>
            <span style={styles.metaLabel}>Published at</span>
            <span style={styles.metaValue}>{new Date(data.published_at).toLocaleString()}</span>
          </div>
          <div style={styles.metaItem}>
            <span style={styles.metaLabel}>Published by</span>
            <span style={styles.metaValue}>{data.published_by ?? '—'}</span>
          </div>
          {data.deprecated_at && (
            <div style={styles.metaItem}>
              <span style={styles.metaLabel}>Deprecated at</span>
              <span style={styles.metaValue}>{new Date(data.deprecated_at).toLocaleString()}</span>
            </div>
          )}
          {data.deprecation_reason && (
            <div style={styles.metaItem}>
              <span style={styles.metaLabel}>Reason</span>
              <span style={styles.metaValue}>{data.deprecation_reason}</span>
            </div>
          )}
        </div>

        {data.changelog && (
          <div style={{ marginBottom: '1.5rem' }}>
            <p style={styles.sectionTitle}>Changelog</p>
            <p style={{ fontSize: '0.875rem', color: tokens.colors.textDark }}>{data.changelog}</p>
          </div>
        )}

        <div>
          <p style={styles.sectionTitle}>Definition</p>
          <pre style={styles.pre} aria-label="Template definition JSON">
            {JSON.stringify(data.definition, null, 2)}
          </pre>
        </div>

        {data.status === 'active' && (
          <div style={styles.actions}>
            <button
              type="button"
              style={styles.btnDanger}
              onClick={onDeprecateClick}
              aria-label="Deprecate template"
            >
              Deprecate
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
